import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@material-ui/core';
import { CsvBuilder } from 'filefy';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAppData } from '../../providers';
import { useStyles } from './styles';
import { PDFFileIcon, ExcelFileIcon, ExportIcon } from '../CustomIcons';
import { trackAmplitudeData } from '../../services/amplitudeService';

interface Props {
  exportFileName: string;
  columns: string[];
  exportTableData: string[][];
  fontSize?: number;
  isEnabled: boolean;
  reportNameInEn: string;
}

export const ExportButtonsWithTitle: FC<Props> = ({ exportFileName, exportTableData, columns, fontSize, isEnabled, reportNameInEn }) => {
  const classes = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const { translations, locale } = useAppData();
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [translation, setTranslation] = useState<any>([]);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const capitalizedColumns = () => columns.map((column) => column?.toUpperCase());

  const handleExportCSVClick = () => {
    trackAmplitudeData('partnerPortal_action_report', {
      type: 'ExportExcel',
      report_name: reportNameInEn,
    });
    const builder = new CsvBuilder(`${exportFileName}.csv`);
    builder.setDelimeter(',').setColumns(capitalizedColumns()).addRows(exportTableData).exportFile();
  };

  const createPDF = (): Promise<any> =>
    new Promise((resolve) => {
      if (jsPDF !== null) {
        const content: {
          startY: number;
          head: string[][];
          body: string[][];
          styles: {
            fontSize?: number;
            font?: string;
          };
        } = {
          startY: 50,
          head: [capitalizedColumns()],
          body: exportTableData,
          styles: { fontSize: fontSize || 10 },
        };

        const unit = 'pt';
        const size = 'A4';
        const orientation = 'landscape';

        // eslint-disable-next-line new-cap
        const doc = new jsPDF(orientation, unit, size);

        if (locale === 'ja') {
          doc.addFont('/NotoSansJP-Regular.ttf', 'NotoSansJP-Regular', 'regular');
          doc.setFont('NotoSansJP-Regular', 'regular');
          content.styles.font = 'NotoSansJP-Regular';
        }

        if (locale === 'ar') {
          doc.addFont('/Amiri-Regular.ttf', 'Amiri-Regular', 'regular');
          doc.setFont('Amiri-Regular', 'regular');
          content.styles.font = 'Amiri-Regular';
        }

        if (locale === 'zh') {
          doc.addFont('/PingFang-SC-Regular.ttf', 'PingFang-SC-Regular', 'regular');
          doc.setFont('PingFang-SC-Regular', 'regular');
          content.styles.font = 'PingFang-SC-Regular';
        }

        if (locale === 'th') {
          doc.addFont('/ThonburiBold.ttf', 'ThonburiBold', 'bold');
          doc.setFont('ThonburiBold', 'bold');
          content.styles.font = 'ThonburiBold';
        }

        if (locale === 'ko') {
          doc.addFont('/NotoSansKR-Regular.ttf', 'NotoSansKR-Regular', 'regular');
          doc.setFont('NotoSansKR-Regular', 'regular');
          content.styles.font = 'NotoSansKR-Regular';
        }

        if (locale === 'vi') {
          doc.addFont('/Roboto-Regular.ttf', 'Roboto-Regular', 'regular');
          doc.setFont('Roboto-Regular', 'regular');
          content.styles.font = 'Roboto-Regular';
        }

        autoTable(doc, content);

        setOpen(false);
        doc.save(`${exportFileName}.pdf`);
        setTimeout(() => {
          resolve('resolved');
        }, 3000);
      }
    });

  const handleExportPDFClick = () => {
    setLoading(true);
    createPDF().then(() => {
      trackAmplitudeData('partnerPortal_action_report', {
        type: 'ExportPDF',
        report_name: reportNameInEn,
      });
      setOpen(false);
      setLoading(false);
    });
  };

  return (
    <>
      <ButtonGroup ref={anchorRef} className={classes.buttonGroup}>
        <Button className={classes.btn} aria-expanded={open ? 'true' : undefined} onClick={handleToggle} disabled={!isEnabled} startIcon={<ExportIcon />}>
          <span>{translation.text_6420}</span>
        </Button>
      </ButtonGroup>
      <Popper open={open} anchorEl={anchorRef.current} transition style={{ zIndex: 6000 }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList className={classes.menuList}>
                  <MenuItem className={classes.menuItem} onClick={handleExportPDFClick} disabled={loading}>
                    <PDFFileIcon />
                    <span className={classes.menuItemText}>{translation.downloadPDF}</span>
                  </MenuItem>
                  <MenuItem className={classes.menuItem} onClick={handleExportCSVClick}>
                    <ExcelFileIcon />
                    <span className={classes.menuItemText}>{translation.downloadExcel}</span>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
